import type { useInvoice } from '@/features/invoices/queries'
import { formatDateOnly, formatMoney } from '@/lib/utils'
import type { ApInvoiceDetail } from '@/features/invoices/view-models'

export function compactDate(value: string | null | undefined) {
  return value ? formatDateOnly(value.slice(0, 10)) : 'Not set'
}

export function parseEmailList(value: string | string[] | null | undefined): string[] {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  const trimmed = value.trim()
  if (!trimmed) return []
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
    } catch {
      // fall through to comma split
    }
  }
  return trimmed
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'Unknown size'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function validateAmount(
  label: string,
  value: string | number | null | undefined,
): string | null {
  if (value === '' || value == null) return null
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount < 0) return `${label} must be a non-negative number`
  return null
}

export function toNullable(value: string) {
  return value.trim() === '' ? null : value
}

export function firstPoLink(invoice: NonNullable<ReturnType<typeof useInvoice>['data']>) {
  return invoice.po_links?.find((link) => link.purchase_order?.uuid || link.po_number_raw)
}

export function projectForPoLink(link: ReturnType<typeof firstPoLink>) {
  return link?.purchase_order?.internal_order?.project || link?.project || null
}

/** When this invoice is due and how much is left, for the folded Payment section. */
export function paymentSummary(invoice: ApInvoiceDetail) {
  if (invoice.status === 'paid') return 'Paid'
  const due = invoice.payment_part
    ? invoice.payment_part.due_date
      ? compactDate(invoice.payment_part.due_date)
      : invoice.payment_part.due_on === 'ship'
        ? 'when it ships'
        : 'on delivery'
    : compactDate(
        invoice.effective_due_date || invoice.priority_due_date || invoice.official_due_date,
      )
  const parts = [`Pay by ${due}`]
  if (
    invoice.remaining_balance != null &&
    Number(invoice.remaining_balance) !== Number(invoice.invoice_total ?? 0)
  )
    parts.push(`${formatMoney(invoice.remaining_balance)} left`)
  return parts.join(' · ')
}
